<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import ChatMessage from '@/components/Sections/Communication/ChatRoom/ChatMessage.vue'
import Backend from '@/models/backend.ts'
import { useChatStore } from '@/stores/chatStore.ts'
import { logger } from '@/models/logger';

/**
 * Stores
 */
const chatStore = useChatStore()

/**
 * Variables
 */
const chatContainer = ref<HTMLDivElement | null>(null)
const isAtBottom = ref(true) // auto‑scroll while true
const isLoadingOlderMessages = ref(false) // prevent duplicate requests

/**
 * Computed
 */
const chatMessages = computed(() => {
  const roomMap = chatStore.roomMessages.get(chatStore.activeRoomId)
  return roomMap ? [...roomMap.values()].sort((a, b) => a.id - b.id) : []
})

/**
 * Methods
 */
const scrollToBottom = async () => {
  const el = chatContainer.value
  if (!el) return

  // Wait for any images in the chat to load before scrolling
  const images = el.querySelectorAll('img')
  const imagePromises = Array.from(images)
    .filter(img => !img.complete)
    .map(img => new Promise(resolve => {
      img.onload = resolve
      img.onerror = resolve // Also resolve on error to prevent hanging
    }))

  if (imagePromises.length > 0) {
    await Promise.all(imagePromises)
  }

  el.scrollTop = el.scrollHeight
}

const handleScroll = () => {
  const el = chatContainer.value
  if (!el) return

  const threshold = 10 // px you accept as “bottom”
  isAtBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold

  // Check if the user has scrolled to the top
  if (el.scrollTop === 0 && !isLoadingOlderMessages.value) {
    loadOlderMessages()
  }
}

const loadMessages = () => {
  if (!chatStore.getMessagesInitialized(chatStore.activeRoomId)) {
    new Backend()
      .postRoomMessages(chatStore.activeRoomId, 0, 30)
      .then(() => {
        chatStore.setMessagesInitialized(chatStore.activeRoomId)
      })
      .catch((err) => logger.error('Error fetching messages:', err))
  }
}

const loadOlderMessages = async () => {
  if (chatStore.getMessagesLoaded(chatStore.activeRoomId)) return
  const el = chatContainer.value
  if (!el) return

  const prevHeight = el.scrollHeight
  const prevScroll = el.scrollTop // 0 when you’re at the very top
  isLoadingOlderMessages.value = true

  try {
    const oldestId = chatMessages.value[0]?.id ?? 0
    const { data } = await new Backend().postRoomMessages(chatStore.activeRoomId, oldestId, 30)

    if (data.messages.length === 0) chatStore.setMessagesLoaded(chatStore.activeRoomId)
  } catch (err) {
    logger.error('Error loading older messages:', err)
  } finally {
    /* ── wait for Vue *and* the browser ── */
    await nextTick() // Vue DOM update
    await new Promise(requestAnimationFrame) // browser layout

    const el2 = chatContainer.value
    if (el2) {
      const delta = el2.scrollHeight - prevHeight // how much taller it got
      el2.scrollTop = prevScroll + delta // keep the same msgs in view
    }
    isLoadingOlderMessages.value = false
  }
}

/**
 * Lifecycle
 */
onMounted(async () => {
  loadMessages()
  await nextTick()
  scrollToBottom()
})

/**
 * Watchers
 */
watch(
  () => chatStore.activeRoomId,
  async () => {
    loadMessages()
    await nextTick()
    scrollToBottom()
  }
)

watch(chatMessages, async () => {
  await nextTick()
  if (isAtBottom.value) scrollToBottom()
})
</script>

<template>
  <StandardCard class="flex-grow" content-height="h-full">
    <div ref="chatContainer" class="chat-limit overflow-y-auto" @scroll="handleScroll">
      <div v-if="chatMessages.length" class="flex flex-col justify-end min-h-full">
        <ChatMessage v-for="msg in chatMessages" :key="msg.id" :message="msg" />
      </div>
    </div>
  </StandardCard>
</template>

<style scoped lang="scss">
.chat-limit {
  /* Use visual viewport height to prevent keyboard jumping */
  height: calc(100dvh - 16.2rem);
  
  /* Fallback for older browsers */
  @supports not (height: 100dvh) {
    height: calc(var(--vh, 1vh) * 100 - 16.2rem);
  }
}
</style>
